/**
 * Versions:
 * 0.01 (9.12.2019)
 */

import { Pool } from "../Collections/Pool";
import { NodeList } from "../Collections/NodeList";
import { Node } from "../Collections/Node";



/**
 * Umoznuje opakovane volat callback po urcitem case
 */
export class TimeLoop {
    /**
     * (RO) Delka jednoho cyklu smycky v ms
     */
    get cycleLength(): number { return this._cycleLen; }
    private _cycleLen: number;

    /**
     * (RO) Zbyvajici pocet opakovani smycky. -1 = smycka se opakuje do nekonecna.
     */
    get remRepeats(): number { return this._remRepeats; }
    private _remRepeats: number;

    /**
     * Zbyvajici cas do dokonceni smycky
     */
    private _remTime: number;

    private _callback: (userData?: any) => void;
    private _callbackCtx: any;

    /**
     * Uzivatelska data, ktera se predaji do callbacku
     */
    private _userData: any;

    /**
     * Pool s volnymi smyckami
     */
    private _freeLoops: Pool<TimeLoop>;

    /**
     * Seznam s aktivnimi smyckami
     */
    private _actLoops: NodeList<TimeLoop>;

    /**
     * Seznam s neaktivnimi smyckami
     */
    private _inactLoops: NodeList<TimeLoop>;

    private _listNode: Node<TimeLoop>;

    private _flags: number;

    /**
     * (RO) True, if the time loop is ready
     */
    get isReady(): boolean { return (this._flags & eFlag.ready) != 0; }
    /**
     * (RO) True, if the time loop has been started
     */
    get isActive(): boolean { return (this._flags & eFlag.started) != 0; }

    /**
     * (RO) True, if the started time loop has been stopped
     */
    get isPaused(): boolean { return (this._flags & eFlag.paused) != 0; }

    //-------------------------------------------------------------------------
    /**
     * Konstruktor
     * @param freeTimers
     * @param actTimers 
     */
    //-------------------------------------------------------------------------
    constructor(freeLoops: Pool<TimeLoop>, actLoops: NodeList<TimeLoop>, inactLoops: NodeList<TimeLoop>) {
        this._freeLoops = freeLoops;
        this._actLoops = actLoops;
        this._inactLoops = inactLoops;
    }

    //-------------------------------------------------------------------------
    /**
     * Vola TimeManager
     */
    //-------------------------------------------------------------------------
    _init(): void {
        this._flags = eFlag.ready;
    }

    //-------------------------------------------------------------------------
    /**
     * Vola TimeManager.
     * @param deltaMs 
     */
    //-------------------------------------------------------------------------
    _update(deltaMs: number): void {
        if ((this._remTime -= deltaMs) <= 0) {
            // Snizit pocet opakovani smycky
            if (this._remRepeats > 0)
                this._remRepeats--;

            // Nastavit cas dalsiho cyklu smycky
            if (this._remRepeats > 0) {
                if ((this._flags & eFlag.preciseTiming) != 0) {
                    this._remTime += this._cycleLen;
                }
                else {
                    this._remTime = this._cycleLen;
                }
            }

            // Callback
            this._callback(this._userData);

            if (this._remRepeats == 0)
                this.release();
        }
    }

    //-------------------------------------------------------------------------
    /**
     * Spusteni smycky
     * @param duration Dobra trvani cyklu smycky v ms
     * @param repeats Pocet opakovani smycky. Pokud < 0, tak se opakuje do nekonecna.
     * @param callback Callback, ktery se vola po kazdem dokonceny cyklu smycky
     * @param userData Uzivatelska data, ktera se predavaji do callbacku
     * @param preciseTiming True, pokud se smycka ma casovat presne. 
     * Presne casovani znamena, ze celkova doba smycky bude duration * repeats, ale prodleva mezi volanim kazdeho dalsiho callbacku bude pravdepodobne kratsi a kratsi.
     * Default: false
     */
    //-------------------------------------------------------------------------
    start(duration: number, repeats: number, callback: (userData?: any) => void, userData?: any, preciseTiming: boolean = false): TimeLoop {
        this.checkValidity();

        if ((this._flags & eFlag.started) != 0)
            throw new Error("TimeLoop has been already started.");

        this._cycleLen = duration;
        this._remTime = duration;

        if ((this._remRepeats = repeats) == 0)
            this._remRepeats = 1;

        this._callback = callback;
        this._userData = userData;

        if (preciseTiming)
            this._flags |= eFlag.preciseTiming;

        this._flags |= eFlag.started;

        this._actLoops.add(this);
        this._listNode = this._actLoops.lastNode;

        return this;
    }

    //-------------------------------------------------------------------------
    /**
     * Pozastaveni behu smycky
     */
    //-------------------------------------------------------------------------
    pause(): TimeLoop {
        this.checkValidity();

        if ((this._flags & (eFlag.started | eFlag.paused)) == eFlag.started) {
            this._flags |= eFlag.paused;
            this._actLoops.removeByNode(this._listNode);
            this._inactLoops.add(this);
            this._listNode = this._inactLoops.lastNode;
        }

        return this;
    }

    //-------------------------------------------------------------------------
    //-------------------------------------------------------------------------
    resume(): TimeLoop {
        this.checkValidity();

        if ((this._flags & eFlag.paused) != 0) {
            this._flags &= ~eFlag.paused;
            this._inactLoops.removeByNode(this._listNode);
            this._actLoops.add(this);
            this._listNode = this._actLoops.lastNode;
        }

        return this;
    }

    //-------------------------------------------------------------------------
    /**
     * Zastaveni smycky a uvolneni jejiho objektu pro nove pouziti.
     * Zastavenou smycku neni mozne dale pouzivat (dojde k jeji vraceni do poolu volnych smycek).
     */
    //-------------------------------------------------------------------------
    release(): void {
        if ((this._flags & eFlag.ready) == 0)
            return;

        // Odebrat smycku ze seznamu
        if ((this._flags & eFlag.paused) == 0) {
            this._actLoops.removeByNode(this._listNode);
        }
        else {
            this._inactLoops.removeByNode(this._listNode);
        }

        this._listNode = undefined;

        // Nastavit smycku jako neinicializovanou
        this._flags = 0;

        // Vratit smycku do poolu volnych smycek
        this._freeLoops.returnItem(this);
    }

    //-------------------------------------------------------------------------
    //-------------------------------------------------------------------------
    private checkValidity(): void {
        if ((this._flags & eFlag.ready) == 0)
            throw new Error("TimeLoop has been released");
    }
}



const enum eFlag {
    started = 1,
    paused = 2,
    preciseTiming = 4,
    ready = 8,
}